import { ArticleCard } from '@americanexpress/consumer-travel-base';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useReactlytics } from '@americanexpress/reactlytics';
import type { MarketingTilesCardProps } from '../../types/components';
import styles from './MarketingTiles.scss';
import { MODULE_NAME } from '../../constants/modules';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { useMemo } from 'react';
import { usePersonaTypeContext } from '../../context/personaTypeContext';

export const MarketingTilesCard = ({
  className,
  content: {
    heading,
    subHeading,
    ctaLabel,
    redirectUrl,
    analyticElement,
    imageUrl,
    imageLocationLabel,
    reactlyticsDetail: reactlyticsDetailProp,
    hasDisplayEarlyTenureBadge,
  },
  index,
}: MarketingTilesCardProps) => {
  const { formatMessage } = useIntl();
  const { track } = useReactlytics();

  const { personaType } = usePersonaTypeContext();

  const locale = useSelector(getActiveLocaleSelector);
  const [, market] = locale.toLowerCase().split('-');
  const analyticsElementPrefix = `${market}hp-marketingtiles-${personaType}`;

  const earlyTenureBadgeLabel = hasDisplayEarlyTenureBadge && formatMessage({ id: 'marketingTile.label' });

  const elementIdentifier = useMemo(() => {
    let elementIdentifier = `${analyticsElementPrefix}-tile${index}`
    if (analyticElement !== undefined && typeof analyticElement === 'string' && analyticElement.trim() !== '') {
      elementIdentifier = elementIdentifier.concat(`-${analyticElement.toLowerCase().replace(/\s+/, '')}`);
    }
    return elementIdentifier;
  }, [analyticsElementPrefix, analyticElement]);

  return (
    <div
      id={`marketingTile${index}`}
      className={clsx('position-relative margin-2-b', styles.marketingTilesCardWrapper, className)}
      data-testid={`marketing-tiles-card-${index}`}
    >
      <ArticleCard
        className={styles.marketingTilesCard}
        title={heading}
        body={subHeading}
        imageUrl={imageUrl}
        badge={earlyTenureBadgeLabel || imageLocationLabel}
        triggerOnClickAnalytics={() => {
          if (elementIdentifier) {
            track({
              event: {
                element: elementIdentifier,
                type: "click",
                identifier: MODULE_NAME,
              },
            })
          }
        }}
        analytics={elementIdentifier && { analyticsElement: elementIdentifier, isTrackImpressions: true }}
        cta={{
          label: ctaLabel,
          link: redirectUrl
        }}
        orientation="vertical"
      />
    </div>
  );
};